import React, { useEffect, useRef, useState } from 'react';
import styles from './MobileApp.module.css';
import AppStore from './assets/app-store.svg';
import GooglePlay from './assets/google-play.svg';
import PhoneMockup from './assets/phone_mockup.png';
import { showToast } from '../../../../utils/toast';
import screenshot1 from './assets/screenshot1.png';
import screenshot2 from './assets/screenshot2.png';

const MobileApp = () => {
  const firstLineRef = useRef(null);
  const secondLineRef = useRef(null);
  const sectionRef = useRef(null);
  const [showSecondScreen, setShowSecondScreen] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          setShowSecondScreen(false);
          timeoutRef.current = setTimeout(() => {
            setShowSecondScreen(true);
          }, 1000);
        } else {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          setShowSecondScreen(false);
        }
      });
    }, { threshold: 0.2 });

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current || !firstLineRef.current || !secondLineRef.current) return;
      
      const sectionRect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      const visiblePercentage = 1 - (sectionRect.top / windowHeight);
      
      const hasScrolledOut = sectionRect.bottom < 0;
      const hasScrolledPast = sectionRect.top > windowHeight;

      if (hasScrolledOut || hasScrolledPast) {
        firstLineRef.current.style.opacity = 0;
        secondLineRef.current.style.opacity = 0;
        return;
      }
        
      const scrollProgress = Math.min(Math.max(visiblePercentage, 0), 1);
      
      const elementHeight = sectionRect.height;
      const visibleTop = Math.max(0, sectionRect.top);
      const visibleBottom = Math.min(windowHeight, sectionRect.bottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
      const visibilityRatio = visibleHeight / elementHeight;
      
      const fadeOutStart = 0.7; 
      const fadeOutFactor = sectionRect.top < 0 
        ? Math.min(Math.max((visibilityRatio - fadeOutStart) / (1 - fadeOutStart), 0), 1)
        : 1;
      
      const opacity = scrollProgress * fadeOutFactor;
      
      const firstLinePosition = 35 - (scrollProgress * 100);
      const secondLinePosition = -35 + (scrollProgress * 100);
      
      firstLineRef.current.style.transform = `translateX(${firstLinePosition}vw)`;
      firstLineRef.current.style.opacity = opacity;
      
      secondLineRef.current.style.transform = `translateX(${secondLinePosition}vw)`;
      secondLineRef.current.style.opacity = opacity;
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <section ref={sectionRef} className={styles.mobileAppSection}>
      <div className={styles.container}>
        <div className={styles.phoneWrapper}>
          <div className={styles.textWrapper}>
            <span ref={firstLineRef} className={styles.textLine}>
              The Gateway to
            </span>
            <span ref={secondLineRef} className={styles.textLine}>
              Your Spatial Experiences
            </span>
          </div>
          
          <div className={styles.phoneMockupWrapper}>
            <img 
              src={PhoneMockup} 
              alt="App Screenshot"
              className={styles.phoneMockup}
            />
            <img 
              src={screenshot1} 
              alt="App Screenshot 1"
              className={`${styles.screenshot} ${!showSecondScreen ? styles.visible : ''}`}
            />
            <img 
              src={screenshot2} 
              alt="App Screenshot 2"
              className={`${styles.screenshot} ${showSecondScreen ? styles.visible : ''}`}
            />
          </div>
        </div>
        
        <div className={styles.storeButtonWrapper}>
          <a 
            onClick={(e) => {
              e.preventDefault();
              showToast.comingSoon();
            }}
            href="#"
          >
            <img src={AppStore} alt="Download on App Store" />
          </a>
          <a 
            onClick={(e) => {
              e.preventDefault();
              showToast.comingSoon();
            }}
            href="#"
          >
            <img src={GooglePlay} alt="Get it on Google Play" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default MobileApp;